import { useEffect, FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";

import { RootState } from "../../store";
import {
  rehydrateSession
} from "../../slices/sessionSlice";
import { setUpgradeModalOpen, setAuthPromptOpen } from "../../slices/utilsSlice";
import OnboardingWizard from "../onboarding/OnboardingWizard";
import LoadingIndicator from "../common/loading-indicator";
import CriticalError from "../common/critical-error-indicator";
import Dialog from "../common/dialog";
import ToastContainer from "../common/toast/ToastContainer";
import ReportProblemButton from "../common/report-problem";
import DeferredActionsExecutor from "../common/deferred-actions/DeferredActionsExecutor";
import UpgradeModal from "../common/upgrade-modal/UpgradeModal";
import AuthPromptModal from "../common/auth-prompt-modal/AuthPromptModal";
import CookieConsentBanner from "../common/cookie-consent-banner";
import { authService } from "../authorization/authService";
import { setUserProperties } from "../../utils/analytics";
// posthog loaded dynamically — 188KB deferred from initial bundle

const PUBLIC_PREFIXES = ["/courses", "/course/", "/exercises", "/catalog-exercise", "/login", "/signup", "/accept-invite", "/datenschutz", "/agb", "/impressum"];

const AppContainer: FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const {
    loading: sessionLoading,
    error: sessionError,
    isAuthenticated,
    user,
  } = useSelector((state: RootState) => state.session);
  const upgradeModalOpen = useSelector((state: RootState) => state.utils.upgradeModalOpen);
  const authPromptOpen = useSelector((state: RootState) => state.utils.authPromptOpen);

  const isPublicRoute = location.pathname === "/auth/callback";
  const isPublicPath = location.pathname === "/" ||
    PUBLIC_PREFIXES.some((p) => location.pathname.startsWith(p));
  // Auth-flow routes where onboarding must NOT be shown (would cause loops)
  const isAuthFlowRoute = isPublicRoute ||
    ["/login", "/signup", "/accept-invite"].some((p) => location.pathname.startsWith(p));

  // Initial session rehydration from localStorage
  useEffect(() => {
    dispatch(rehydrateSession());
    sessionStorage.removeItem("chunk_reload_attempted");
  }, [dispatch]);

  // Initialize Cognito auth state on mount
  useEffect(() => {
    authService.initAuthState();
    return () => {
      authService.cleanup();
    };
  }, []);

  // Set GA4 user properties whenever the authenticated user changes
  useEffect(() => {
    if (isAuthenticated && user) {
      setUserProperties({
        userId: user.uid,
        userRole: user.role,
        subscriptionTier: user.subscription?.tier?.toLowerCase() ?? "free",
        preferredLanguage: user.preferred_language,
      });
      // PostHog user identification (dynamic import to keep out of initial bundle)
      import("posthog-js").then(({ default: posthog }) => {
        posthog.identify(user.uid, {
          email: user.email,
          name: user.name,
          role: user.role,
          subscription_tier: user.subscription?.tier?.toLowerCase() ?? "free",
          preferred_language: user.preferred_language,
        });
      });
    }
  }, [isAuthenticated, user?.uid, user?.role, user?.subscription?.tier]);

  // Don't block public routes while session loads
  if (sessionLoading && !isPublicPath && !isPublicRoute) {
    return <LoadingIndicator />;
  }

  if (sessionError && !isPublicPath) {
    return <CriticalError />;
  }

  const needsOnboarding = isAuthenticated && user?.onboarding_completed === false;

  return (
    <div>
      <Dialog />
      <ToastContainer />
      <ReportProblemButton />
      <CookieConsentBanner />
      <UpgradeModal isOpen={upgradeModalOpen} onClose={() => dispatch(setUpgradeModalOpen(false))} />
      {authPromptOpen && !isAuthenticated && (
        <AuthPromptModal reason="generic" onClose={() => dispatch(setAuthPromptOpen(false))} />
      )}
      {isAuthenticated && <DeferredActionsExecutor />}
      {needsOnboarding && !isAuthFlowRoute ? (
        <OnboardingWizard />
      ) : (
        <Outlet />
      )}
    </div>
  );
};

export default AppContainer;
